<template>
  <div class="row h-100">
    <div class="col-12 h-100 d-flex flex-column">
      <div class="d-flex justify-content-end align-items-center mb-3 pb-1">
        <b-button v-if="isSubmitting" variant="primary" size="lg" class="pl-4 pr-4 ml-3" disabled>
          <b-spinner small />
          {{ 'saving' | i18n }}
        </b-button>
        <b-button
          v-else
          variant="primary"
          size="lg"
          class="pl-4 pr-4 ml-3"
          :disabled="!ticketData.title || !ticketData.templateId"
          @click="submitTicket"
        >
          {{ 'save' | i18n }}
        </b-button>
      </div>
      <div class="overflow-auto pr-1 overflow-x-hidden min-h-75">
        <Loader v-if="!caseLoaded" />
        <div v-else>
          <BlockHeader name="Details" />
          <div class="row">
            <div class="col-lg-8 col-sm-12 col-xs-12">
              <Input v-model="ticketData.title" form-group-id="Title" placeholder="Title" :required="true" />
            </div>
            <div class="col-lg-4 col-sm-12 col-xs-12">
              <Select
                v-model="ticketData.templateId"
                :options="templateOptions"
                form-group-id="template"
                placeholder="template"
                :view-mode="isViewTemplateId"
                :required="true"
                :on-change="
                  (value) => {
                    const currentTemplate = templates.find(({ id }) => value === id);
                    if (currentTemplate.defaultTeam) ticketData.team.id = currentTemplate.defaultTeam;
                    if (currentTemplate.defaultUser) ticketData.user.id = currentTemplate.defaultUser;
                  }
                "
              />
            </div>
          </div>
          <div class="row justify-content-between">
            <div class="col-lg-6 col-sm-12 col-xs-12">
              <Select
                v-model="ticketData.team.id"
                :options="teamOptions"
                form-group-id="Team"
                placeholder="Team"
                :on-change="
                  () => {
                    ticketData.user.id = null;
                  }
                "
              />
            </div>
            <div class="col-lg-6 col-sm-12 col-xs-12">
              <Select
                v-model="ticketData.user.id"
                :options="usersOptions"
                form-group-id="User Responsible"
                placeholder="User Responsible"
                :disabled="!usersOptions.length"
              />
            </div>
          </div>
        </div>
        <div v-if="selectedTemplate">
          <BlockHeader name="Template Details" />
          <Group
            v-for="group in selectedTemplate.groups"
            :key="group.id"
            :group="group"
            :set-field-value="setFieldValue"
            :fields-value="fieldsValue"
            :lists="lists"
            :getFileURL="getFileURL"
            :getFile="getFile"
            groupName="Group"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

import api from '../../../api';
import { VALIDATION_RULES, ENTRY_TYPES, ENTRY_TYPES_FILES } from '../../../../../../../constants';
import { ReportsMixin, AuthStorageMixin, NotificationMixin } from '../../../../mixins';
import Group from '../../reports/cases/components/group';
import Loader from '../../../components/common/loader';
import Select from '../../../components/common/select';
import Input from '../../../components/common/input';
import BlockHeader from '../../../components/common/block-header';

export default {
  name: 'TicketDetails',
  components: { Group, Loader, Select, Input, BlockHeader },
  mixins: [ReportsMixin, AuthStorageMixin, NotificationMixin],
  data() {
    return {
      templateOptions: [],
      teams: [],
      templates: [],
      ticketData: {
        title: '',
        templateId: null,
        user: {
          id: null,
        },
        team: {
          id: null,
        },
      },
      isViewTemplateId: false,
      caseLoaded: false,
      lists: [],
      fieldsValue: {},
      access_token_link: '',
      isSubmitting: false,
    };
  },
  computed: {
    selectedTemplate: function () {
      if (!this.ticketData.templateId && !this.ticketData.template) return null;
      const template = this.ticketData.template || this.templates.find(({ id }) => id === this.ticketData.templateId);
      if (!template) return null;

      const groups = [];
      const groupsFields = {};

      template.fields.forEach((f) => {
        if (!groupsFields[f.groupId]) {
          groupsFields[f.groupId] = [];
        }
        f.required = f.rules.some((r) => r.name === VALIDATION_RULES.REQUIRED);
        if (f.editByMobile) {
          groupsFields[f.groupId].push(f);
        }
      });

      template.groups.forEach((g) => {
        const data = {
          name: g.name,
          id: g.id,
          fields: groupsFields[g.id],
        };
        groups.push(data);
      });

      return {
        name: template.name,
        id: template.id,
        groups,
      };
    },
    teamOptions: function () {
      return this.teams.map((item) => ({ value: item.id, text: item.name }));
    },
    usersOptions: function () {
      const team = this.ticketData.team || {};
      const teamID = team.id;
      const selectedTeam = this.teams.find((i) => i.id === teamID) || {};
      const currentUsers = selectedTeam.users;
      return currentUsers ? currentUsers.map((item) => ({ value: item.id, text: item.name })) : [];
    },
  },
  async created() {
    if (this.$route.params.id) {
      const { data } = await api.ticket.getById(this.$route.params.id);
      this.ticketData.title = data.title;
      this.ticketData.templateId = data.template.id;
      this.ticketData.template = data.template;
      this.ticketData.team.id = data.team.id;
      this.ticketData.user.id = data.user.id;
      this.fieldsValue = data.report.fields;
      this.isViewTemplateId = true;
    }

    await this.loadTemplatesLookup();
    await this.loadTeamsLookup();
  },
  methods: {
    getFileURL(id) {
      const auth = this.authStorageService.getAuth();
      const accessTokenLink = auth.access_token_link;
      const baseURL = axios.defaults.baseURL;
      const url = this.$route.params.id
        ? `${baseURL}/api/v1/tickets/ticket/${this.$route.params.id}/files/${id}?authorization=${accessTokenLink}`
        : `${baseURL}/api/v1/tickets/templates/${this.ticketData.templateId}/files/${id}?authorization=${accessTokenLink}`;
      return url;
    },
    async getFile(id) {
      try {
        await axios(this.getFileURL(id));
        window.open(this.getFileURL(id), '_blank');
      } catch (error) {
        this.showNotification({ type: 'error', message: this.$translate('file_do_not_uploaded') });
      }
    },
    async loadTemplatesLookup() {
      const templateResponse = await api.template.get({});
      this.templates = templateResponse.data;
      this.templateOptions = templateResponse.data.map((item) => ({ value: item.id, text: item.name }));
    },
    async loadTeamsLookup() {
      const teamsResponse = await this.reportsService.getTeams();
      this.teams = teamsResponse;
      this.caseLoaded = true;
    },
    setFieldValue(field, value) {
      const isFile = ENTRY_TYPES_FILES.includes(field.entryType);
      if (isFile && this.$route.params.id && value) {
        this.fieldsValue[field.id] = value;
      } else {
        this.fieldsValue[field.key] = value;
      }
    },
    mapFieldValue(field) {
      if (field.entryType === ENTRY_TYPES.PICTURE_IN_TEMPLATE || field.entryType === ENTRY_TYPES.PDF_ATTACHMENT) {
        return field.file;
      }
      if (typeof field.value == 'undefined') {
        return null;
      }
      if (field.entryType === ENTRY_TYPES.LIST) {
        if (typeof field.value === 'string') {
          field.value = JSON.parse(field.value);
        }
        return JSON.stringify(
          field.value
            ? field.value.map((i) => ({ id: i.id, identifier: i.identifier, title: i.title, subtitle: i.subtitle, content: i.content }))
            : field.value
        );
      }
      if (field.entryType === ENTRY_TYPES.DATE) {
        return moment(field.value).format('YYYY-MM-DD');
      }
      if (field.entryType === ENTRY_TYPES.DATE_TIME) {
        return moment.utc(field.value).local().format();
      }
      const toString = [ENTRY_TYPES.LOCATION, ENTRY_TYPES.TIME, ENTRY_TYPES.MULTIPLE_CHECKBOXES];
      if (toString.includes(field.entryType)) {
        return JSON.stringify(field.value);
      }
      return field.value;
    },
    mapNewTicketPayload(params) {
      const payload = new FormData();
      payload.append('title', params.title);
      payload.append('templateId', params.templateId);
      payload.append('teamId', params.teamId);
      payload.append('userId', params.userId);
      params.fields.forEach((field) => {
        payload.append(`field_${field.key}`, this.mapFieldValue(field));
        if (field.showTextInput) {
          payload.append(`field_${field.key}_textInputValue`, typeof field.textInputValue !== 'undefined' ? field.textInputValue : null);
        }
      });
      return payload;
    },
    generateNewTicketData() {
      const ticketData = {
        title: this.ticketData.title,
        templateId: this.ticketData.templateId,
        teamId: this.ticketData.team.id,
        userId: this.ticketData.user.id,
      };
      const fieldsList = [];
      this.selectedTemplate.groups.forEach(({ fields }) => {
        if (fields) {
          fields.forEach((fieldObj) => {
            fieldsList.push({
              ...fieldObj,
              value: this.fieldsValue[fieldObj.key] || this.fieldsValue[fieldObj.id],
            });
          });
        }
      });
      ticketData.fields = fieldsList;

      return ticketData;
    },
    async submitTicket() {
      try {
        this.isSubmitting = true;
        const ticketData = this.generateNewTicketData();
        const ticketPayload = this.mapNewTicketPayload(ticketData);

        if (this.$route.params.id) {
          await api.ticket.update(ticketPayload, this.$route.params.id);
        } else {
          await api.ticket.create(ticketPayload);
        }

        this.showNotification({ type: 'success', message: this.$translate('success') });
        this.$router.push({ name: 'tickets' });
      } catch (error) {
        this.showNotification({ type: 'error', message: error.message });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}
::v-deep .user-block {
  font-size: 13px;
  font-weight: 400;
}
.overflow-x-hidden {
  overflow-x: hidden !important;
}
.min-h-75 {
  min-height: 75%;
}
</style>
